import { useMemo } from 'react';
import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { toLocalYMD } from '../utils/hydration';
import {
  type MealPlan, type PlanEntry, type PlanOccasion, type PlanRun, type PlanState,
  PLAN_DAYS_MAX, PLAN_PORTION_MAX, addDays, canPlanOn, cleanOccasionName, cleanPlanName, hasStarted, occasionsOf, planDayOn,
  planIdOf, resolvePlanRow, samePlanName, upcomingRun,
} from '../utils/nutritionPlan';
import { SAVE_ERROR, type WriteResult } from './nutritionStore';
import { readOccasionType, type OccasionType } from '../utils/nutrition';

export const PLAN_LOAD_ERROR = 'Couldn’t load your meal plan. Check your connection and try again.';

interface PlanRow { id: string; name: string; days: number; archived_at: string | null; created_at: string }
interface RunRow { id: string; plan_id: string | null; start_date: string; repeats: boolean; end_date: string | null; created_at: string }
interface PlanMealRow { plan_id: string; meal_id: string }
/** `kind` is mig 0298, `plan_id` mig 0299 — the loads read `*`. */
interface OccasionRow { id: string; name: string; position: number; archived_at: string | null; kind?: string | null; plan_id?: string | null }
interface EntryRow {
  id: string; occasion_id: string; scope: 'day' | 'date' | 'weekly'; day_index?: number | null; weekday: number | null; plan_date: string | null;
  effective_from: string; meal_id: string | null; portion: number | string | null; created_at: string;
}

export const rowToPlan = (r: PlanRow): MealPlan => ({ id: r.id, name: r.name, days: r.days, archivedAt: r.archived_at, createdAt: r.created_at });
export const rowToRun = (r: RunRow): PlanRun => ({ id: r.id, planId: r.plan_id, startDate: r.start_date, repeats: r.repeats, endDate: r.end_date, createdAt: r.created_at });
export const rowToOccasion = (r: OccasionRow): PlanOccasion => ({
  id: r.id, name: r.name, position: r.position, archivedAt: r.archived_at, kind: readOccasionType(r.kind), planId: r.plan_id ?? null,
});
export function rowToEntry(r: EntryRow): PlanEntry {
  const portion = r.portion === null ? null : Number(r.portion);
  return {
    id: r.id, occasionId: r.occasion_id, scope: r.scope, dayIndex: r.day_index ?? null, weekday: r.weekday, planDate: r.plan_date,
    effectiveFrom: r.effective_from, mealId: r.meal_id, portion: portion !== null && Number.isFinite(portion) ? portion : null,
    createdAt: r.created_at,
  };
}

/** A plan change: one occasion — only on `date`, or Day `dayIndex` of its plan from `date` on. */
export interface PlanChange {
  occasionId: string;
  scope: 'date' | 'day';
  date: string;
  /** Required for 'day'. */
  dayIndex?: number;
  /** Null = "nothing planned". */
  mealId: string | null;
  portion: number | null;
}

/** The occasions editor's result: the list as it should be, in order (new rows have no id). */
export type OccasionDraft = { id?: string; name: string; kind?: OccasionType | null }[];

/** A new plan: its name and length, optionally its first occasions, meals — and a start. */
export interface NewPlan {
  name: string;
  days: number;
  occasions?: { name: string; kind?: OccasionType | null }[];
  mealIds?: string[];
  /** Start it at once on this date (repeating). */
  startOn?: string;
}

interface NutritionPlanStore extends PlanState {
  plans: MealPlan[];
  runs: PlanRun[];
  /** Each plan's meal list. */
  planMeals: { planId: string; mealId: string }[];
  occasions: PlanOccasion[];
  entries: PlanEntry[];
  isLoaded: boolean;
  loadFailed: boolean;
  isLoading: boolean;
  initializedUserId: string | null;

  initialize: () => Promise<void>;
  reload: () => Promise<void>;
  /** Save a plan's occasions: new ones inserted, renamed/moved updated, missing ones ARCHIVED. */
  saveOccasions: (planId: string, draft: OccasionDraft) => Promise<WriteResult>;
  /** One insert — a plan's history is never edited. */
  setPlan: (change: PlanChange) => Promise<WriteResult<PlanEntry>>;
  createPlan: (plan: NewPlan) => Promise<WriteResult<MealPlan>>;
  /** Rename or change the length (rows past a shorter length are kept, just not shown). */
  updatePlan: (planId: string, patch: { name?: string; days?: number }) => Promise<WriteResult>;
  /** A copy that isn't active: occasions, meal list and the schedule as it stands today. */
  duplicatePlan: (planId: string) => Promise<WriteResult<MealPlan>>;
  /** Start a plan on a date — repeating, or stopping after its last day. */
  startPlan: (planId: string, startDate: string, repeats: boolean) => Promise<WriteResult<PlanRun>>;
  /** No plan from this date (a stop). */
  stopPlans: (fromDate: string) => Promise<WriteResult>;
  /** Call off a start (or stop) that hasn't happened yet — its dates go back to what decided them before (mig 0300). */
  cancelRun: (runId: string) => Promise<WriteResult>;
  /** Never started → its scheduled start cancelled and the plan deleted. Started → archived (stopped after today if running; a start still to come cancelled). */
  deletePlan: (planId: string) => Promise<WriteResult<'deleted' | 'archived'>>;
  restorePlan: (planId: string) => Promise<WriteResult>;
  addPlanMeals: (planId: string, mealIds: string[]) => Promise<WriteResult>;
  removePlanMeal: (planId: string, mealId: string) => Promise<WriteResult>;
}

const fail = (error = SAVE_ERROR) => ({ ok: false as const, error });
const PARTIAL = 'Couldn’t save every change — what’s shown now is what was saved. Check your connection and try again.';

/** The store's rows as a `PlanState`, stable between renders. */
export function usePlanState(): PlanState {
  const plans = useNutritionPlanStore(s => s.plans);
  const runs = useNutritionPlanStore(s => s.runs);
  const occasions = useNutritionPlanStore(s => s.occasions);
  const entries = useNutritionPlanStore(s => s.entries);
  return useMemo(() => ({ plans, runs, occasions, entries }), [plans, runs, occasions, entries]);
}

/**
 * ⭐ Nutrition — meal plans (phase 3a 2026-10-09; named plans 2026-10-17). Server rows
 * only (`nutrition_plans`, `nutrition_plan_runs`, `nutrition_plan_meals`,
 * `nutrition_plan_occasions`, `nutrition_plan_entries`; migs 0292 + 0299). Writes are NOT
 * optimistic: a change appears once the database has it, and a refused one says so (the
 * plan is read days ahead, so a phantom row would mislead for a week). A change in
 * several writes that fails part-way reloads, so the screen shows exactly what was saved.
 */
export const useNutritionPlanStore = create<NutritionPlanStore>((set, get) => {
  const load = async (userId: string): Promise<boolean> => {
    const by = (t: string, order: string, ascending = true) => supabase.from(t).select('*').eq('user_id', userId).order(order, { ascending });
    const [pl, ru, pm, occ, ent] = await Promise.all([
      by('nutrition_plans', 'created_at'),
      by('nutrition_plan_runs', 'created_at'),
      by('nutrition_plan_meals', 'created_at'),
      by('nutrition_plan_occasions', 'position'),
      by('nutrition_plan_entries', 'created_at'),
    ]);
    const error = pl.error ?? ru.error ?? pm.error ?? occ.error ?? ent.error;
    if (error) {
      console.error('Error loading the meal plan:', error);
      set({ loadFailed: true, isLoaded: true });
      return false;
    }
    set({
      plans: ((pl.data ?? []) as PlanRow[]).map(rowToPlan),
      runs: ((ru.data ?? []) as RunRow[]).map(rowToRun),
      planMeals: ((pm.data ?? []) as PlanMealRow[]).map(r => ({ planId: r.plan_id, mealId: r.meal_id })),
      occasions: ((occ.data ?? []) as OccasionRow[]).map(rowToOccasion),
      entries: ((ent.data ?? []) as EntryRow[]).map(rowToEntry),
      loadFailed: false,
      isLoaded: true,
    });
    return true;
  };

  /** Run a write that may take several steps; on any failure reload and say so. */
  const steps = async <T,>(userId: string, work: () => Promise<T>, label: string): Promise<WriteResult<T>> => {
    try {
      const value = await work();
      await load(userId);
      return { ok: true, value };
    } catch (err) {
      console.error(`Error saving the meal plan (${label}):`, err);
      await load(userId);
      return fail(PARTIAL);
    }
  };
  const must = <T,>(r: { data: T | null; error: unknown }): T => { if (r.error || r.data === null) throw r.error ?? new Error('no row'); return r.data; };
  const ok = (r: { error: unknown }) => { if (r.error) throw r.error; };

  const nameTaken = (name: string, except?: string) =>
    get().plans.some(p => !p.archivedAt && p.id !== except && samePlanName(p.name, name));
  const validDays = (n: number) => Number.isInteger(n) && n >= 1 && n <= PLAN_DAYS_MAX;
  const uniqueCopyName = (base: string) => {
    for (let i = 1; ; i++) {
      const n = (i === 1 ? `${base} (copy)` : `${base} (copy ${i})`).slice(0, 60);
      if (!nameTaken(n)) return n;
    }
  };

  const insertRun = async (userId: string, planId: string | null, startDate: string, repeats: boolean, days: number) =>
    rowToRun(must(await supabase.from('nutrition_plan_runs').insert({
      user_id: userId, plan_id: planId, start_date: startDate,
      repeats: planId ? repeats : false,
      end_date: planId && !repeats ? addDays(startDate, days - 1) : null,
    }).select('*').single()) as RunRow);

  return {
    plans: [],
    runs: [],
    planMeals: [],
    occasions: [],
    entries: [],
    isLoaded: false,
    loadFailed: false,
    isLoading: false,
    initializedUserId: null,

    initialize: async () => {
      if (get().isLoading) return;
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return;
      if (get().initializedUserId === user.id && !get().loadFailed) return;
      if (get().initializedUserId !== user.id) set({ plans: [], runs: [], planMeals: [], occasions: [], entries: [], isLoaded: false });
      set({ isLoading: true });
      try {
        await load(user.id);
        set({ initializedUserId: user.id });
      } finally {
        set({ isLoading: false });
      }
    },

    reload: async () => {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return;
      await load(user.id);
    },

    saveOccasions: async (planId, draft) => {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return fail();
      if (!get().plans.some(p => p.id === planId)) return fail('That plan is no longer here.');
      const names = draft.map(d => cleanOccasionName(d.name));
      if (names.some(n => n === null)) return fail('Every occasion needs a name.');
      const mine = get().occasions.filter(o => planIdOf(o, get().plans) === planId);
      const before = new Map(mine.map(o => [o.id, o]));
      const now = new Date().toISOString();
      const r = await steps(user.id, async () => {
        // Archive the ones left out (kept for history — past plans keep their name).
        for (const o of mine) {
          if (!o.archivedAt && !draft.some(d => d.id === o.id)) {
            ok(await supabase.from('nutrition_plan_occasions').update({ archived_at: now, updated_at: now }).eq('id', o.id));
          }
        }
        for (let i = 0; i < draft.length; i++) {
          const d = draft[i]; const name = names[i]!;
          // ⚠️ `kind` (mig 0298) is sent only when there is one to write or change.
          const kind = d.kind ?? null;
          if (!d.id) {
            ok(await supabase.from('nutrition_plan_occasions').insert({ user_id: user.id, plan_id: planId, name, position: i, ...(kind ? { kind } : {}) }));
          } else {
            const o = before.get(d.id);
            const kindChanged = d.kind !== undefined && kind !== (o?.kind ?? null);
            // An old-app occasion (no plan) is adopted by the plan it's shown in.
            const adopt = !!o && !o.planId;
            if (o && (o.name !== name || o.position !== i || kindChanged || adopt)) {
              ok(await supabase.from('nutrition_plan_occasions')
                .update({ name, position: i, updated_at: now, ...(kindChanged ? { kind } : {}), ...(adopt ? { plan_id: planId } : {}) }).eq('id', d.id));
            }
          }
        }
      }, 'occasions');
      return r.ok ? { ok: true, value: undefined } : r;
    },

    setPlan: async (c) => {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return fail();
      const today = toLocalYMD(new Date());
      if (!canPlanOn(c.date, today)) return fail('Past days can’t be changed — they’re history.');
      const occasion = get().occasions.find(o => o.id === c.occasionId && !o.archivedAt);
      if (!occasion) return fail('That occasion is no longer in your plan.');
      const plan = get().plans.find(p => p.id === planIdOf(occasion, get().plans));
      if (c.scope === 'day' && !(plan && Number.isInteger(c.dayIndex) && c.dayIndex! >= 1 && c.dayIndex! <= plan.days)) {
        return fail('That day isn’t in this plan.');
      }
      if (c.mealId !== null && !(typeof c.portion === 'number' && c.portion > 0 && c.portion <= PLAN_PORTION_MAX)) {
        return fail(`Choose a portion between 0 and ${PLAN_PORTION_MAX} servings.`);
      }
      const row = {
        user_id: user.id,
        occasion_id: c.occasionId,
        scope: c.scope,
        day_index: c.scope === 'day' ? c.dayIndex : null,
        weekday: null,
        plan_date: c.scope === 'date' ? c.date : null,
        effective_from: c.date,
        meal_id: c.mealId,
        portion: c.mealId === null ? null : Math.round((c.portion as number) * 100) / 100,
      };
      const { data, error } = await supabase.from('nutrition_plan_entries').insert(row).select('*').single();
      if (error || !data) { console.error('Error saving the meal plan:', error); return fail(); }
      const entry = rowToEntry(data as EntryRow);
      set({ entries: [...get().entries, entry] });
      // A meal planned from outside the plan's list joins it (the picker's "Show all your meals").
      if (plan && c.mealId && !get().planMeals.some(m => m.planId === plan.id && m.mealId === c.mealId)) {
        const { error: e2 } = await supabase.from('nutrition_plan_meals').insert({ plan_id: plan.id, meal_id: c.mealId, user_id: user.id });
        if (!e2) set({ planMeals: [...get().planMeals, { planId: plan.id, mealId: c.mealId }] });
      }
      return { ok: true, value: entry };
    },

    createPlan: async (d) => {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return fail();
      const name = cleanPlanName(d.name);
      if (!name) return fail('Give the plan a name.');
      if (nameTaken(name)) return fail(`You already have a plan called “${name}”.`);
      if (!validDays(d.days)) return fail(`A plan is 1 to ${PLAN_DAYS_MAX} days long.`);
      const occ = (d.occasions ?? []).map(o => ({ name: cleanOccasionName(o.name), kind: o.kind ?? null }));
      if (occ.some(o => !o.name)) return fail('Every occasion needs a name.');
      const today = toLocalYMD(new Date());
      if (d.startOn && !canPlanOn(d.startOn, today)) return fail('A plan can’t start on a past day.');
      return steps(user.id, async () => {
        const plan = rowToPlan(must(await supabase.from('nutrition_plans').insert({ user_id: user.id, name, days: d.days }).select('*').single()) as PlanRow);
        if (occ.length) {
          ok(await supabase.from('nutrition_plan_occasions').insert(occ.map((o, i) => ({ user_id: user.id, plan_id: plan.id, name: o.name, position: i, ...(o.kind ? { kind: o.kind } : {}) }))));
        }
        const mealIds = [...new Set(d.mealIds ?? [])];
        if (mealIds.length) ok(await supabase.from('nutrition_plan_meals').insert(mealIds.map(m => ({ plan_id: plan.id, meal_id: m, user_id: user.id }))));
        if (d.startOn) await insertRun(user.id, plan.id, d.startOn, true, d.days);
        return plan;
      }, 'create');
    },

    updatePlan: async (planId, patch) => {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return fail();
      const plan = get().plans.find(p => p.id === planId);
      if (!plan) return fail('That plan is no longer here.');
      const next: Record<string, unknown> = {};
      if (patch.name !== undefined) {
        const name = cleanPlanName(patch.name);
        if (!name) return fail('Give the plan a name.');
        if (nameTaken(name, planId)) return fail(`You already have a plan called “${name}”.`);
        if (name !== plan.name) next.name = name;
      }
      if (patch.days !== undefined) {
        if (!validDays(patch.days)) return fail(`A plan is 1 to ${PLAN_DAYS_MAX} days long.`);
        if (patch.days !== plan.days) next.days = patch.days;
      }
      if (!Object.keys(next).length) return { ok: true, value: undefined };
      const { error } = await supabase.from('nutrition_plans').update({ ...next, updated_at: new Date().toISOString() }).eq('id', planId);
      if (error) { console.error('Error saving the plan:', error); return fail(); }
      set({ plans: get().plans.map(p => (p.id === planId ? { ...p, ...(next as Partial<MealPlan>) } : p)) });
      return { ok: true, value: undefined };
    },

    duplicatePlan: async (planId) => {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return fail();
      const src = get().plans.find(p => p.id === planId);
      if (!src) return fail('That plan is no longer here.');
      const today = toLocalYMD(new Date());
      const srcOcc = occasionsOf(get(), planId);
      const entries = get().entries;
      const meals = get().planMeals.filter(m => m.planId === planId).map(m => m.mealId);
      return steps(user.id, async () => {
        const plan = rowToPlan(must(await supabase.from('nutrition_plans').insert({ user_id: user.id, name: uniqueCopyName(src.name), days: src.days }).select('*').single()) as PlanRow);
        for (const o of srcOcc) {
          const copy = must(await supabase.from('nutrition_plan_occasions')
            .insert({ user_id: user.id, plan_id: plan.id, name: o.name, position: o.position, ...(o.kind ? { kind: o.kind } : {}) }).select('*').single()) as OccasionRow;
          // The latest schedule, Day by Day (one-day changes stay with the original).
          const rows = [];
          for (let day = 1; day <= src.days; day++) {
            const r = resolvePlanRow(entries.filter(e => e.scope !== 'date'), o.id, '9999-12-31', day);
            if (r?.mealId) rows.push({ user_id: user.id, occasion_id: copy.id, scope: 'day', day_index: day, weekday: null, plan_date: null, effective_from: today, meal_id: r.mealId, portion: r.portion });
          }
          if (rows.length) ok(await supabase.from('nutrition_plan_entries').insert(rows));
        }
        if (meals.length) ok(await supabase.from('nutrition_plan_meals').insert(meals.map(m => ({ plan_id: plan.id, meal_id: m, user_id: user.id }))));
        return plan;
      }, 'duplicate');
    },

    startPlan: async (planId, startDate, repeats) => {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return fail();
      const plan = get().plans.find(p => p.id === planId && !p.archivedAt);
      if (!plan) return fail('That plan is no longer here.');
      if (!canPlanOn(startDate, toLocalYMD(new Date()))) return fail('A plan can’t start on a past day.');
      try {
        const run = await insertRun(user.id, planId, startDate, repeats, plan.days);
        set({ runs: [...get().runs, run] });
        return { ok: true, value: run };
      } catch (err) {
        console.error('Error starting the plan:', err);
        return fail();
      }
    },

    stopPlans: async (fromDate) => {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return fail();
      if (!canPlanOn(fromDate, toLocalYMD(new Date()))) return fail('A plan can’t stop on a past day.');
      try {
        const run = await insertRun(user.id, null, fromDate, false, 1);
        set({ runs: [...get().runs, run] });
        return { ok: true, value: undefined };
      } catch (err) {
        console.error('Error stopping the plan:', err);
        return fail();
      }
    },

    cancelRun: async (runId) => {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return fail();
      const run = get().runs.find(r => r.id === runId);
      if (!run) return fail('That start is no longer here.');
      const started = 'It has already started, so it can’t be cancelled — start another plan instead.';
      if (run.startDate <= toLocalYMD(new Date())) return fail(started);
      const { data, error } = await supabase.from('nutrition_plan_runs').delete().eq('id', runId).select('id');
      if (error) { console.error('Error cancelling the start:', error); return fail(); }
      // RLS refuses silently (0 rows) once the day has come by the database's clock.
      if (!Array.isArray(data) || !data.length) return fail(started);
      set({ runs: get().runs.filter(r => r.id !== runId) });
      return { ok: true, value: undefined };
    },

    deletePlan: async (planId) => {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return fail();
      const plan = get().plans.find(p => p.id === planId);
      if (!plan) return fail('That plan is no longer here.');
      const today = toLocalYMD(new Date());
      const cancel = async (id: string) => {
        const { data, error } = await supabase.from('nutrition_plan_runs').delete().eq('id', id).select('id');
        if (error || !Array.isArray(data) || !data.length) throw error ?? new Error('start not cancelled');
      };
      if (!hasStarted(get().runs, planId, today)) {
        // Never started: call off any scheduled start, then delete it (it decided no day).
        const scheduled = get().runs.filter(r => r.planId === planId);
        return steps(user.id, async () => {
          for (const r of scheduled) await cancel(r.id);
          ok(await supabase.from('nutrition_plans').delete().eq('id', planId));
          return 'deleted' as const;
        }, 'delete');
      }
      const running = planDayOn(get(), today)?.plan.id === planId;
      const upcoming = upcomingRun(get().runs, today);
      return steps(user.id, async () => {
        // It ran: archive — its days stay in history. Running? It stops after today.
        // Due to start again? That start is called off (the plan running then carries on).
        if (running) await insertRun(user.id, null, addDays(today, 1), false, 1);
        else if (upcoming?.planId === planId) await cancel(upcoming.id);
        const now = new Date().toISOString();
        ok(await supabase.from('nutrition_plans').update({ archived_at: now, updated_at: now }).eq('id', planId));
        return 'archived' as const;
      }, 'archive');
    },

    restorePlan: async (planId) => {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return fail();
      const plan = get().plans.find(p => p.id === planId);
      if (!plan) return fail('That plan is no longer here.');
      if (nameTaken(plan.name, planId)) return fail(`You already have a plan called “${plan.name}” — rename that one first.`);
      const { error } = await supabase.from('nutrition_plans').update({ archived_at: null, updated_at: new Date().toISOString() }).eq('id', planId);
      if (error) { console.error('Error restoring the plan:', error); return fail(); }
      set({ plans: get().plans.map(p => (p.id === planId ? { ...p, archivedAt: null } : p)) });
      return { ok: true, value: undefined };
    },

    addPlanMeals: async (planId, mealIds) => {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return fail();
      const fresh = [...new Set(mealIds)].filter(m => !get().planMeals.some(x => x.planId === planId && x.mealId === m));
      if (!fresh.length) return { ok: true, value: undefined };
      const { error } = await supabase.from('nutrition_plan_meals').insert(fresh.map(m => ({ plan_id: planId, meal_id: m, user_id: user.id })));
      if (error) { console.error('Error saving the plan’s meals:', error); return fail(); }
      set({ planMeals: [...get().planMeals, ...fresh.map(m => ({ planId, mealId: m }))] });
      return { ok: true, value: undefined };
    },

    removePlanMeal: async (planId, mealId) => {
      const { user } = await syncManager.safeAuthCheck();
      if (!user) return fail();
      const { error } = await supabase.from('nutrition_plan_meals').delete().eq('plan_id', planId).eq('meal_id', mealId);
      if (error) { console.error('Error saving the plan’s meals:', error); return fail(); }
      set({ planMeals: get().planMeals.filter(m => !(m.planId === planId && m.mealId === mealId)) });
      return { ok: true, value: undefined };
    },
  };
});
